// StepDescribe — step 1 of the wizard. Collects the essentials: project
// `name`, `problem_statement`, and an optional industry `vertical`, plus
// drag-and-drop upload of supporting/format documents.
//
// The problem statement is the important one: as the user types it, the
// parent page re-runs auto-detection over it (shape guess, regulatory
// frameworks, and key "nouns"/domain terms). Those results flow back in via
// the `detected` prop and drive later steps, so everything downstream keys
// off what is written here.
import { Info } from 'lucide-react'

import { InputUploader } from '@/components/intake/InputUploader'
import type { Detected, NewProjectForm, UpdateForm } from '../types'

const VERTICALS = [
  'Energy & Utilities',
  'Healthcare',
  'Fintech',
  'Government',
  'Logistics',
  'Agriculture',
  'Manufacturing',
  'Urban Planning',
  'Education',
  'Media',
  'Defense',
  'Research',
]

export function StepDescribe({
  form,
  update,
  detected,
}: {
  form: NewProjectForm
  update: UpdateForm
  detected: Detected
}) {
  return (
    <>
      <header className="step-header">
        <div className="step-section-tag">
          <Info size={14} />
          Describe the project
        </div>
        <h1 className="step-h1">What are we <em>building</em>?</h1>
        <p className="step-lede">
          Give the project a name and a clear problem statement. We detect the{' '}
          <span className="accent">shape, regulations and key nouns</span> from what you write here —
          everything downstream keys off it.
        </p>
      </header>

      <section className="essential">
        <div className="essential-head">
          <Info size={18} />
          <h2>Essential Information</h2>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="npw-name">Project Name</label>
          <input
            id="npw-name"
            className="field-input"
            type="text"
            placeholder="e.g. Carbon-Neutral Data Center Alpha"
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="npw-problem">Problem Statement</label>
          <textarea
            id="npw-problem"
            className="field-textarea"
            placeholder="Define the core challenge this project addresses…"
            value={form.problem_statement}
            onChange={(e) => update({ problem_statement: e.target.value })}
          />
        </div>
      </section>

      <section className="vertical-section">
        <div className="vertical-label">Industry Vertical</div>
        <div className="chips">
          {VERTICALS.map((v) => (
            <button
              key={v}
              type="button"
              className={`chip${form.vertical === v ? ' selected' : ''}`}
              onClick={() => update({ vertical: form.vertical === v ? null : v })}
            >
              {v}
            </button>
          ))}
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 32 }}>
        <InputUploader
          kind="content"
          mode="pending"
          title="Supporting Documents"
          description="Drag PDFs, spreadsheets, or technical drawings here to provide context."
          hint="Feeds Phase 2 (Observer)"
          files={form.contentFiles}
          onChange={(next) => update({ contentFiles: next })}
        />
        <InputUploader
          kind="format"
          mode="pending"
          title="Formatting References"
          description="Templates or style guides that should shape the exported document."
          hint="Informs Phase 5 export styling"
          files={form.formatFiles}
          onChange={(next) => update({ formatFiles: next })}
        />
      </div>

      {detected.nouns.length > 0 && (
        <section className="detected-panel">
          <div className="detected-eyebrow">Detected from your description</div>
          <div className="detected-row">
            <span className="detected-row-label">Nouns to freeze <span className="arrow">→</span></span>
            <div className="detected-chips">
              {detected.nouns.slice(0, 10).map((n) => (
                <span key={n} className="detected-chip frozen">{n}</span>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
